import React from 'react';

import Modal from '@tv2/lib-offspring/components/messages/Modal';
import { logout } from '@tv2/lib-offspring/api/user/auth';
import { CloseBasicNext } from '@tv2/ui-system/icons/next/navigation/CloseBasicNext';

import { Prompt } from '../usePrompt';
import { ButtonFragmentUI } from './ButtonFragmentUI';

type Props = {
  prompt: Prompt;
  extendedTextVisited: boolean;
  openExtenedInfo: () => void;
};

export function DefaultLayout(props: Props) {
  const {
    prompt: {
      data: { layout: prompt, colorTheme },
      action,
      closePrompt,
      track,
    },
    openExtenedInfo,
    extendedTextVisited,
  } = props;

  const theme =
    colorTheme === 'Dark'
      ? 'theme-dark text-white bg-midnight-900'
      : 'theme-light text-midnight bg-white';

  const actionHandler = makeActionHandler(closePrompt, track, action);
  const closedByDismiss = () => {
    actionHandler(props.prompt.data?.blurAction);
  };

  return (
    <Modal noDismiss={!props.prompt.data?.blurAction} onClose={closedByDismiss}>
      <div
        className={`relative flex flex-col my-0 mx-auto w-full w-max-[590px] w-max-[min(93vw, 590px)] ${theme}`}
      >
        <PromptTop prompt={prompt} actionHandler={actionHandler} />
        <TextPart prompt={prompt} />
        <ExtendedInfo
          openExtenedInfo={openExtenedInfo}
          extendedInfo={prompt.extendedInfo}
          extendedTextVisited={extendedTextVisited}
        />
        <Buttons
          actionHandler={actionHandler}
          buttons={props.prompt.data.layout.buttons}
          colorTheme={colorTheme}
        />
      </div>
      {prompt.helpInfo ? <HelpInfo prompt={prompt} /> : null}
    </Modal>
  );
}

function HelpInfo({ prompt }: { prompt: Prompt['data']['layout'] }) {
  if (!prompt.helpInfo) {
    return null;
  }
  return (
    <div className="theme-dark mt-shared-spacing-dynamic-s text-white">
      <div
        className="text-center text-marker-s link-inline-standalone"
        dangerouslySetInnerHTML={{ __html: prompt.helpInfo }}
      />
    </div>
  );
}

function makeActionHandler(
  closePrompt: () => void,
  track: (identifier: string, details: any) => void,
  serverActionHandler: (action: string) => void
) {
  return (action: Prompt['data']['blurAction']) => {
    if (!action) {
      return;
    }
    if (action.serverAction) {
      serverActionHandler(action.serverAction.mutationPayload);
    }
    // tracking
    track(action?.tracking?.identifier, action?.tracking?.details);

    switch (action?.clientAction.__typename) {
      case 'CloseClientAction': {
        closePrompt();
        break;
      }
      case 'IgnoreClientAction': {
        break;
      }
      case 'LogoutClientAction': {
        logout();
        break;
      }
      case 'GoToClientAction': {
        window.location.href = action.clientAction.destination;
        break;
      }
    }
  };
}

function ExtendedInfo({
  openExtenedInfo,
  extendedTextVisited,
  extendedInfo,
}: {
  openExtenedInfo: () => void;
  extendedInfo: Prompt['data']['layout']['extendedInfo'];
  extendedTextVisited: boolean;
}) {
  if (!extendedInfo) {
    return null;
  }

  return (
    <div>
      <div
        className={`px-shared-spacing-dynamic-l pt-shared-spacing-static-s link-button cursor-pointer text-body-xs ${
          extendedTextVisited ? 'link-button-visited' : ''
        }`}
        onClick={openExtenedInfo}
      >
        <span className="link-inline">{extendedInfo.linkText}</span>
      </div>
    </div>
  );
}

function Buttons({
  buttons,
  actionHandler,
}: {
  buttons: Prompt['data']['layout']['buttons'];
  actionHandler: ReturnType<typeof makeActionHandler>;
  colorTheme: string;
}) {
  return (
    <section className="flex justify-center justify-items-center pt-shared-spacing-dynamic-l pb-shared-spacing-dynamic-xl">
      {buttons.map((button, index) => (
        <div className="mx-2" key={`${button.text}-${index}`}>
          <ButtonFragmentUI
            onClick={() => {
              actionHandler(button.action);
            }}
            btn={button}
          />
        </div>
      ))}
    </section>
  );
}

function TextPart({ prompt }: { prompt: Prompt['data']['layout'] }) {
  return (
    <section className="px-shared-spacing-dynamic-l">
      <h3 className="text-heading-l pt-shared-spacing-dynamic-l">{prompt.title}</h3>
      {prompt.subTitle ? (
        <div className="text-body-s pt-shared-spacing-dynamic-m link-inline">
          <div dangerouslySetInnerHTML={{ __html: prompt.subTitle }} />
        </div>
      ) : null}
      {prompt.text && (
        <div
          className="text-body-xs link-inline pt-shared-spacing-static-s"
          dangerouslySetInnerHTML={{ __html: prompt.text }}
        />
      )}
    </section>
  );
}

function PromptTop({
  prompt,
  actionHandler,
}: {
  prompt: Prompt['data']['layout'];
  actionHandler: ReturnType<typeof makeActionHandler>;
}) {
  return (
    <>
      {prompt.closeButton ? (
        <div
          role="button"
          onKeyDown={undefined}
          onClick={() => actionHandler(prompt.closeButton)}
          tabIndex={0}
          title="Luk info"
          className="block cursor-pointer w-[36px] h-[36px] z-[3] absolute overflow-visible top-4 right-4"
        >
          <div className="w-full h-full opacity-70 rounded-full flex items-center justify-center">
            <CloseBasicNext className="invert" height={16} width={16} />
          </div>
        </div>
      ) : null}
      {prompt.imageUrl ? <img src={prompt.imageUrl} alt="presentation" className="w-full" /> : null}
      {prompt.logoUrl ? (
        <div>
          <img
            src={prompt.logoUrl}
            alt="presentation"
            className="mx-shared-spacing-dynamic-l h-10 mt-shared-spacing-dynamic-l "
          />
        </div>
      ) : null}
    </>
  );
}
